/** Lightweight referral capture: safe to run before market/auth initialization. */
const REF_COOKIE = "lecmo_ref";
const PENDING_COOKIE = "lecmo_ref_pending";
const DEFAULT_COOKIE_DAYS = 30;
const DAY_MS = 86_400_000;

export interface AmbassadorReferralLanding {
  code: string;
  href: string;
  referrer: string;
  capturedAt: number;
}

function normalizeCode(value: unknown): string | null {
  return typeof value === "string" && /^[A-Z0-9-]{2,32}$/i.test(value)
    ? value.toUpperCase()
    : null;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  try {
    const part = document.cookie.split(";").map((entry) => entry.trim())
      .find((entry) => entry.startsWith(`${name}=`));
    return part ? decodeURIComponent(part.slice(name.length + 1)) : null;
  } catch { return null; }
}

function writeCookie(name: string, value: string, expiresAt: number) {
  if (typeof document === "undefined") return;
  try {
    const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${new Date(expiresAt).toUTCString()}; path=/; SameSite=Lax${secure}`;
  } catch { /* A blocked cookie must not break navigation or checkout. */ }
}

function readPending(): { code: string; capturedAt: number } | null {
  try {
    const raw = readCookie(PENDING_COOKIE);
    if (!raw) return null;
    const pending = JSON.parse(raw);
    const code = normalizeCode(pending?.code);
    const capturedAt = pending?.capturedAt;
    if (!code || typeof capturedAt !== "number" || !Number.isFinite(capturedAt)) return null;
    if (capturedAt > Date.now() || capturedAt + DEFAULT_COOKIE_DAYS * DAY_MS <= Date.now()) return null;
    return { code, capturedAt };
  } catch { return null; }
}

/**
 * Cookies are attribution claims, not reward authorization. The purchase
 * handler independently requires an approved ambassador before awarding credit.
 * A separate pending cookie preserves the previous referral's original expiry
 * if the new link later proves invalid; logging outages do not erase either.
 */
export function captureAmbassadorReferral(): AmbassadorReferralLanding | null {
  if (typeof window === "undefined") return null;
  try {
    const href = window.location.href;
    const code = normalizeCode(new URL(href).searchParams.get("ref"));
    if (!code) return null;
    const capturedAt = Date.now();
    writeCookie(PENDING_COOKIE, JSON.stringify({ code, capturedAt }), capturedAt + DEFAULT_COOKIE_DAYS * DAY_MS);
    return { code, href, referrer: document.referrer, capturedAt };
  } catch { return null; }
}

export function readAmbassadorReferral(): string | null {
  return readPending()?.code ?? normalizeCode(readCookie(REF_COOKIE));
}

export function settleAmbassadorReferral(
  landing: AmbassadorReferralLanding,
  result: { tracked?: boolean; reason?: string; cookieDays?: number },
): void {
  const current = readPending();
  // A slow result from a previous link cannot overwrite the latest landing.
  if (!current || current.code !== landing.code || current.capturedAt !== landing.capturedAt) return;
  if (result.reason === "invalid_code") {
    writeCookie(PENDING_COOKIE, "", 0);
    return;
  }
  // These responses all follow the server's approved-code lookup. Transport
  // failures keep the bounded pending claim and do not renew an older cookie.
  if (!result.tracked && result.reason !== "duplicate" && result.reason !== "db_error") return;
  const days = typeof result.cookieDays === "number" && result.cookieDays >= 1 && result.cookieDays <= 120
    ? result.cookieDays : DEFAULT_COOKIE_DAYS;
  writeCookie(REF_COOKIE, landing.code, landing.capturedAt + days * DAY_MS);
  writeCookie(PENDING_COOKIE, "", 0);
}
